import { useState, type FormEvent } from 'react';
import { Foutmelding, Kaart, KaartKop, Knop, KnopLink, Leeg, PaginaKop, Schakelaar, Veld, useToast } from '../components/ui';
import { datumVeld, naamVoluit, tijdVeld, toonNaam } from '../lib/format';
import { verdeelTeams } from '../lib/prestaties';
import { usePouleLeden, useLeague, useRecordMatch } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { usePoule } from '../lib/poule';
import type { Mode } from '../lib/types';
import { valideerPotje, type PotjeFormulier } from '../lib/validate';

function leegFormulier(pouleId: string | null): PotjeFormulier {
  const nu = new Date();
  return {
    pouleId,
    mode: '1v1',
    a1: '',
    a2: '',
    b1: '',
    b2: '',
    scoreA: '',
    scoreB: '',
    datum: datumVeld(nu),
    tijd: tijdVeld(nu),
    teltMee: true
  };
}

export default function NieuwPotje() {
  const { pouleId, poule } = usePoule();
  const { players, rating, zoekSpeler } = useLeague(pouleId);
  const { profile } = useAuth();
  const ikId = profile?.id ?? '';
  const leden = usePouleLeden();
  const vastleggen = useRecordMatch();
  const meld = useToast();
  const [f, setF] = useState<PotjeFormulier>(() => leegFormulier(pouleId));
  const [fout, setFout] = useState<string | null>(null);

  const zet = (velden: Partial<PotjeFormulier>) => setF((oud) => ({ ...oud, ...velden }));

  /* Alleen wie in deze poule speelt kan opgesteld worden. */
  const actief = players.filter(
    (p) => p.isActive && (leden.data ?? []).some((l) => l.pouleId === pouleId && l.profielId === p.id)
  );

  if (!pouleId) {
    return (
      <>
        <PaginaKop titel="Uitslag invoeren" />
        <Kaart>
          <Leeg tekst="Een potje hoort bij een poule, en je zit er nog in geen." ikoon="poule" />
          <div className="text-center">
            <KnopLink to="/poules" variant="primair">
              Naar poules
            </KnopLink>
          </div>
        </Kaart>
      </>
    );
  }

  if (actief.length < 2) {
    return (
      <>
        <PaginaKop titel="Uitslag invoeren" onder={poule?.naam} />
        <Kaart>
          <Leeg tekst="Deze poule heeft minstens twee deelnemers nodig." ikoon="spelers" />
          <div className="text-center">
            <KnopLink to={`/poules/${pouleId}`} variant="primair">
              Iemand uitnodigen
            </KnopLink>
          </div>
        </Kaart>
      </>
    );
  }

  const tweeTegenTwee = f.mode === '2v2';
  const gekozen = [f.a1, f.a2, f.b1, f.b2].filter(Boolean);

  /* Vier spelers zijn op drie manieren te verdelen; de eerlijkste staat vooraan. */
  const indelingen = tweeTegenTwee && gekozen.length === 4 ? verdeelTeams(gekozen, rating) : [];
  const eerlijkst = indelingen[0] ?? null;
  const alEerlijk =
    Boolean(eerlijkst) &&
    [f.a1, f.a2].every((id) => eerlijkst?.teamA.includes(id) || eerlijkst?.teamB.includes(id)) &&
    ([f.a1, f.a2].every((id) => eerlijkst?.teamA.includes(id)) ||
      [f.a1, f.a2].every((id) => eerlijkst?.teamB.includes(id)));

  async function verstuur(e: FormEvent) {
    e.preventDefault();
    /* De poule komt uit de balk en kan na het openen nog binnendruppelen; het
       formulier houdt hem dus niet zelf vast. */
    const uitkomst = valideerPotje({ ...f, pouleId });
    if (!uitkomst.ok) {
      setFout(uitkomst.fout);
      return;
    }
    setFout(null);
    try {
      await vastleggen.mutateAsync(uitkomst.potje);

      /*
       * De uitslag staat er, maar telt nog niet: de tegenpartij moet hem eerst
       * erkennen. Dat zeggen we hier met zoveel woorden, anders zoekt iemand
       * zich rot naar waarom zijn rating niet beweegt.
       */
      const tegenpartij = (
        uitkomst.potje.teamA.includes(ikId) ? uitkomst.potje.teamB : uitkomst.potje.teamA
      )
        .map((id) => toonNaam(zoekSpeler(id)))
        .join(' & ');

      meld(`Vastgelegd. ${tegenpartij} moet de stand nog bevestigen.`);

      /* Spelers blijven staan — meestal volgt er nog een potje. */
      zet({ scoreA: '', scoreB: '', tijd: tijdVeld(new Date()) });
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Opslaan mislukte.');
    }
  }

  const spelerKeuze = (naam: 'a1' | 'a2' | 'b1' | 'b2') => (
    <select
      className="veld"
      value={f[naam]}
      onChange={(e) => zet({ [naam]: e.target.value } as Partial<PotjeFormulier>)}
    >
      <option value="">— kies speler —</option>
      {actief
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((p) => !gekozen.includes(p.id) || p.id === f[naam])
        .map((p) => (
          <option key={p.id} value={p.id}>
            {naamVoluit(p)}
          </option>
        ))}
    </select>
  );

  const kant = (letter: 'A' | 'B') => (
    <Kaart className="flex-1">
      <KaartKop titel={`Team ${letter}`} />
      {/* De volgorde is de opstelling: eerst achterin, dan voorin. Zo weet de
          app op welke stang iemand stond en kan het profiel laten zien waar
          iemand het beste uit de verf komt. */}
      <Veld label={tweeTegenTwee ? 'Verdediger' : 'Speler'}>
        {spelerKeuze(letter === 'A' ? 'a1' : 'b1')}
      </Veld>
      {tweeTegenTwee ? (
        <Veld label="Aanvaller">{spelerKeuze(letter === 'A' ? 'a2' : 'b2')}</Veld>
      ) : null}
      <Veld label="Eindstand">
        <input
          type="number"
          min={0}
          max={10}
          inputMode="numeric"
          className="veld nums w-24 py-2.5 text-center text-xl font-semibold"
          value={letter === 'A' ? f.scoreA : f.scoreB}
          onChange={(e) => zet(letter === 'A' ? { scoreA: e.target.value } : { scoreB: e.target.value })}
        />
      </Veld>
    </Kaart>
  );

  return (
    <>
      <PaginaKop
        titel="Uitslag invoeren"
        onder={`In ${poule?.naam ?? 'deze poule'} · vul de eindstand in nadat het potje gespeeld is. Terugdateren mag.`}
      />

      <form onSubmit={verstuur}>
        {fout ? <Foutmelding>{fout}</Foutmelding> : null}

        <Kaart>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Schakelaar<Mode>
              waarde={f.mode}
              onKies={(mode) => zet({ mode, ...(mode === '1v1' ? { a2: '', b2: '' } : {}) })}
              opties={[
                { waarde: '1v1', label: '1 tegen 1' },
                { waarde: '2v2', label: '2 tegen 2' }
              ]}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-krijt-500">Gespeeld op</span>
              <input
                type="date"
                className="veld w-auto"
                value={f.datum}
                onChange={(e) => zet({ datum: e.target.value })}
              />
              <input type="time" className="veld w-auto" value={f.tijd} onChange={(e) => zet({ tijd: e.target.value })} />
            </div>
          </div>
        </Kaart>

        {eerlijkst ? (
          <div className="mt-3.5">
            <Kaart>
              <KaartKop
                titel="Eerlijke teams"
                hint="op basis van de huidige rating"
                actie={
                  alEerlijk ? (
                    <span className="text-xs font-semibold text-veld-700">dit is al de eerlijkste indeling</span>
                  ) : (
                    <Knop
                      type="button"
                      onClick={() =>
                        zet({
                          a1: eerlijkst.teamA[0],
                          a2: eerlijkst.teamA[1],
                          b1: eerlijkst.teamB[0],
                          b2: eerlijkst.teamB[1]
                        })
                      }
                    >
                      Zo verdelen
                    </Knop>
                  )
                }
              />
              <div className="flex flex-col gap-1.5">
                {indelingen.map((ind, i) => (
                  <div
                    key={`${ind.teamA[0]}-${ind.teamA[1]}`}
                    className={`flex flex-wrap items-center gap-x-2 gap-y-1 border px-3 py-2 text-sm ${
                      i === 0 ? 'border-krijt-900 bg-krijt-50' : 'border-krijt-200'
                    }`}
                  >
                    <span className="font-semibold">
                      {ind.teamA.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}
                    </span>
                    <span className="text-krijt-500">tegen</span>
                    <span className="font-semibold">
                      {ind.teamB.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}
                    </span>
                    <span className="nums ml-auto text-xs text-krijt-500">
                      {Math.round(ind.verschil)} punten verschil · {Math.round(ind.kansA)}–{100 - Math.round(ind.kansA)}
                    </span>
                  </div>
                ))}
              </div>
            </Kaart>
          </div>
        ) : null}

        <div className="mt-3.5 grid gap-3.5 md:grid-cols-2">
          {kant('A')}
          {kant('B')}
        </div>

        <label className="mt-4 flex w-fit cursor-pointer items-start gap-2.5 rounded-sm border border-krijt-200 px-3 py-2.5">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={f.teltMee === false}
            onChange={(e) => zet({ teltMee: !e.target.checked })}
          />
          <span className="text-sm">
            <span className="font-medium">Telt niet mee voor de ranglijst</span>
            <span className="block text-xs text-krijt-500">
              Het potje komt wel in de historie, maar raakt niemands rating aan. Er hoeft dan ook niemand
              akkoord te geven.
            </span>
          </span>
        </label>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Knop variant="primair" type="submit" disabled={vastleggen.isPending}>
            {vastleggen.isPending ? 'Bezig…' : 'Uitslag vastleggen'}
          </Knop>
          <Knop
            type="button"
            onClick={() => {
              setF(leegFormulier(pouleId));
              setFout(null);
            }}
          >
            Formulier leegmaken
          </Knop>
          <span className="ml-auto text-xs text-krijt-500">
            Elo: start 800, K-factor 32, bij 2v2 telt het teamgemiddelde.
          </span>
        </div>
      </form>
    </>
  );
}
